import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * PROVEEDORES — el core NO tiene colección `proveedores`.
 * Los proveedores existen como campos embebidos en `compras`:
 *   proveedorRuc, proveedorRazonSocial
 *
 * El panel deriva el maestro desde compras. No se inventa una colección nueva.
 * Alta de proveedor = registrar una compra (u OCR) con esos campos.
 */

export function mapProveedorFromCompra(c) {
  const ruc = String(c.proveedorRuc || '').trim();
  const nombre = String(c.proveedorRazonSocial || '').trim();
  if (!ruc && !nombre) return null;
  return {
    key: ruc || nombre.toLowerCase(),
    proveedorRuc: ruc || '—',
    proveedorRazonSocial: nombre || 'Sin razón social',
  };
}

/** Lista única de proveedores observados en compras del tenant. */
export async function listSuppliers({ ruc, pageSize = 50, search = '' }) {
  if (!ruc) throw new Error('RUC requerido');
  const snap = await getDocs(
    query(collection(db, 'compras'), where('ruc', '==', ruc), orderBy('createdAt', 'desc'), limit(200))
  );

  const seen = new Map();
  for (const d of snap.docs) {
    const p = mapProveedorFromCompra(d.data());
    if (!p) continue;
    if (!seen.has(p.key)) {
      seen.set(p.key, { ...p, comprasCount: 0, totalCompras: 0, ultimaCompra: null });
    }
    const acc = seen.get(p.key);
    acc.comprasCount += 1;
    acc.totalCompras += Number(d.data().total || 0);
    const fecha = d.data().fechaEmision || d.data().createdAt;
    if (!acc.ultimaCompra) acc.ultimaCompra = fecha || null;
  }

  let items = [...seen.values()];
  if (search) {
    const term = search.trim().toLowerCase();
    items = items.filter(
      (p) =>
        p.proveedorRazonSocial.toLowerCase().includes(term) ||
        p.proveedorRuc.toLowerCase().includes(term)
    );
  }

  return { items: items.slice(0, pageSize), hasMore: items.length > pageSize };
}

/** Compras de un proveedor (por RUC o nombre). */
export async function listSupplierPurchases({ ruc, proveedorRuc, proveedorRazonSocial, pageSize = 10 }) {
  if (!ruc) throw new Error('RUC requerido');
  const snap = await getDocs(
    query(collection(db, 'compras'), where('ruc', '==', ruc), orderBy('createdAt', 'desc'), limit(100))
  );
  const termRuc = String(proveedorRuc || '').trim();
  const termName = String(proveedorRazonSocial || '').trim().toLowerCase();
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => {
      if (termRuc && termRuc !== '—') return String(c.proveedorRuc || '').trim() === termRuc;
      return String(c.proveedorRazonSocial || '').trim().toLowerCase() === termName;
    })
    .slice(0, pageSize);
}
